// Resolves the per-menu channel_id (menucode) the backend requires on almost
// every admin/portal endpoint. The menu tree comes from admin/menu/list; each
// child / sub_child carries a menu_definition (the route path) and a menu_code
// (the channel_id for that page). We cache these so any page can send channel_id
// without depending on the user having clicked that menu item first — which is
// why the "Please send proper channel id" error used to appear after a refresh
// or when a page was opened via a direct URL.

const CHID_SUFFIX = '_CHID';
const MENU_TREE_KEY = 'MENU_TREE';

// Same key derivation used by handleClick / ApiDataService / MapModuleFlow.
const chidKey = (path) => (path || '').replace(/\//g, '').toUpperCase() + CHID_SUFFIX;

// Walk every child and sub_child, invoking cb(menu_definition, menu_desc, menu_code).
const walkMenu = (tree, cb) => {
  if (!Array.isArray(tree)) return;
  tree.forEach((parent) => {
    (parent.child || []).forEach((child) => {
      if (child.menu_definition) cb(child.menu_definition, child.menu_desc, child.menu_code);
      (child.sub_child || []).forEach((sub) => {
        if (sub.menu_definition) cb(sub.menu_definition, sub.menu_desc, sub.menu_code);
      });
    });
  });
};

// Call once the menu list is loaded: cache the whole tree and pre-populate a
// <PATH>_CHID entry for every menu item so channel_id is resolvable on every
// page immediately, without waiting for a click.
export const cacheMenuTree = (tree) => {
  if (!Array.isArray(tree)) return;
  try {
    localStorage.setItem(MENU_TREE_KEY, JSON.stringify(tree));
  } catch (e) { /* storage disabled/full – keep pre-populating individual keys */ }
  walkMenu(tree, (path, desc, code) => {
    if (!code) return;
    localStorage.setItem(chidKey(path), JSON.stringify({ menuname: desc, menucode: code }));
  });
};

// Resolve { menuname, menucode } for a path: prefer the exact <PATH>_CHID entry
// (set by a click or by cacheMenuTree), else fall back to the cached menu tree.
export const resolveMenu = (pathname) => {
  const key = chidKey(pathname);
  const stored = localStorage.getItem(key);
  if (stored) {
    try { return JSON.parse(stored); } catch (e) { /* corrupt – fall through */ }
  }
  const treeRaw = localStorage.getItem(MENU_TREE_KEY);
  if (treeRaw) {
    try {
      let found = null;
      walkMenu(JSON.parse(treeRaw), (path, desc, code) => {
        if (!found && chidKey(path) === key) found = { menuname: desc, menucode: code };
      });
      if (found) {
        localStorage.setItem(key, JSON.stringify(found)); // cache for next time
        return found;
      }
    } catch (e) { /* corrupt tree – ignore */ }
  }
  return null;
};

// Convenience: just the channel_id for the current/given path ('' if unknown).
export const resolveChannelId = (pathname) => {
  const menu = resolveMenu(pathname);
  return menu ? menu.menucode : '';
};
